"use client";

import { useState } from "react";
import { Checkbox, SelectField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import { FractionProblemView } from "@/components/tools/worksheets-math/fraction-view";
import {
  AnswerList,
  CommonControls,
  defaultSettings,
  ProblemCell,
  ProblemGrid,
  UnmetNotice,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  type DenominatorMode,
  equationText,
  type FractionOptions,
  type FractionSheetType,
  generateFractions,
  type SimplifyMode,
} from "@/lib/tool-kit/worksheets/fractions";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const FRACTION_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Fraction Practice",
  titles: {
    add: "Adding Fractions",
    subtract: "Subtracting Fractions",
    multiply: "Multiplying Fractions",
    divide: "Dividing Fractions",
    simplify: "Simplifying Fractions",
    equivalent: "Equivalent Fractions",
    compare: "Comparing Fractions",
    mixed: "Fraction Operations",
  },
  typeLabel: "Worksheet type",
  types: {
    add: "Add fractions",
    subtract: "Subtract fractions",
    multiply: "Multiply fractions",
    divide: "Divide fractions",
    simplify: "Simplify fractions",
    equivalent: "Find equivalent fractions",
    compare: "Compare fractions (<, >, =)",
    mixed: "Mixed operations",
  },
  presetsLabel: "Quick levels",
  presets: {
    addLike: "Add, same denominator",
    addUnlike: "Add, unlike denominators",
    subtractRelated: "Subtract, related denominators",
    multiply: "Multiply",
    simplify: "Simplify",
    compare: "Compare",
  },
  denominatorsLabel: "Denominators",
  denominators: {
    same: "Same denominator",
    related: "Related — one is a multiple of the other",
    different: "Any different denominators",
  },
  maxDenominatorLabel: "Largest denominator",
  answersLabel: "Answers",
  answers: {
    any: "Either way",
    required: "Always need simplifying",
    none: "Already in lowest terms",
  },
  improper: "Allow improper fractions and answers of 1 or more",
} as const;

type SheetType = FractionSheetType;
type Fields = Omit<FractionOptions, "count">;

const START: Fields = {
  type: "add",
  denominators: "related",
  maxDenominator: 12,
  answers: "any",
  improper: true,
};

const PRESETS: Record<
  keyof typeof FRACTION_WORKSHEET_GENERATOR_STRINGS.presets,
  Partial<Fields>
> = {
  addLike: {
    type: "add",
    denominators: "same",
    maxDenominator: 8,
    improper: false,
  },
  addUnlike: { type: "add", denominators: "related", maxDenominator: 12 },
  subtractRelated: {
    type: "subtract",
    denominators: "related",
    maxDenominator: 12,
  },
  multiply: { type: "multiply", maxDenominator: 10 },
  simplify: { type: "simplify", maxDenominator: 24, improper: false },
  compare: {
    type: "compare",
    denominators: "different",
    maxDenominator: 12,
    improper: false,
  },
};

const TYPES: SheetType[] = [
  "add",
  "subtract",
  "multiply",
  "divide",
  "simplify",
  "equivalent",
  "compare",
  "mixed",
];
const DENOMINATOR_MAXIMA = [4, 5, 6, 8, 10, 12, 16, 20, 24];
const SHORT_TYPES = new Set<SheetType>(["simplify", "equivalent", "compare"]);

function autoColumns(type: SheetType, fontSize: string): number {
  if (SHORT_TYPES.has(type)) {
    return fontSize === "large" ? 3 : 4;
  }
  return fontSize === "small" ? 3 : 2;
}

export default function FractionWorksheetGeneratorWidget() {
  const strings = useToolUi(FRACTION_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(defaultSettings({ count: 16 }));
  const seed = useWorksheetSeed();
  const [fields, setFields] = useState<Fields>(START);

  const patch = (next: Partial<Fields>) =>
    setFields((current) => ({ ...current, ...next }));
  const generated = generateFractions(
    { ...fields, count: settings.count },
    createRng(seed.seed)
  );
  const usesDenominators =
    fields.type === "add" ||
    fields.type === "subtract" ||
    fields.type === "compare" ||
    fields.type === "mixed";
  const usesAnswers = !SHORT_TYPES.has(fields.type);
  const columns =
    settings.columns > 0
      ? settings.columns
      : autoColumns(fields.type, settings.fontSize);
  const title = settings.title.trim() || strings.titles[fields.type];

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="space-y-2">
          <p className={LABEL}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => (
              <button
                className={BUTTON_SMALL}
                key={key}
                onClick={() => patch({ ...START, ...PRESETS[key] })}
                type="button"
              >
                {strings.presets[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="fw-type"
            label={strings.typeLabel}
            onChange={(type: SheetType) => patch({ type })}
            options={TYPES.map((value) => ({
              value,
              label: strings.types[value],
            }))}
            value={fields.type}
          />
          <SelectField
            id="fw-max-den"
            label={strings.maxDenominatorLabel}
            onChange={(value) => patch({ maxDenominator: Number(value) })}
            options={DENOMINATOR_MAXIMA.map((value) => ({
              value: String(value),
              label: String(value),
            }))}
            value={String(fields.maxDenominator)}
          />
          {usesDenominators ? (
            <SelectField
              id="fw-denominators"
              label={strings.denominatorsLabel}
              onChange={(denominators: DenominatorMode) =>
                patch({ denominators })
              }
              options={(["same", "related", "different"] as const).map(
                (value) => ({ value, label: strings.denominators[value] })
              )}
              value={fields.denominators}
            />
          ) : null}
          {usesAnswers ? (
            <SelectField
              id="fw-answers"
              label={strings.answersLabel}
              onChange={(answers: SimplifyMode) => patch({ answers })}
              options={(["any", "required", "none"] as const).map((value) => ({
                value,
                label: strings.answers[value],
              }))}
              value={fields.answers}
            />
          ) : null}
        </div>
        <Checkbox
          checked={fields.improper}
          label={strings.improper}
          onChange={(improper) => patch({ improper })}
        />
        <CommonControls
          countChoices={[8, 10, 12, 16, 20, 24, 30, 36]}
          idPrefix="fw"
          layouts={false}
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
        <UnmetNotice strings={strings} unmet={generated.unmet} />
      </div>
      <WorksheetSheets
        answers={
          <AnswerList
            columns={fields.type === "compare" ? 4 : 3}
            entries={generated.problems.map(equationText)}
          />
        }
        problems={
          <ProblemGrid columns={columns} gap="gap-x-6 gap-y-7">
            {generated.problems.map((problem, index) => (
              <ProblemCell
                // biome-ignore lint/suspicious/noArrayIndexKey: problems are positional
                key={index}
                number={settings.numbered ? index + 1 : null}
              >
                <FractionProblemView problem={problem} />
              </ProblemCell>
            ))}
          </ProblemGrid>
        }
        seed={seed.seed}
        settings={settings}
        strings={strings}
        title={title}
      />
    </div>
  );
}
